import TextButton from '@/shared/components/buttons/TextButton'
import React, {
  ForwardedRef,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { PatientProfileValidator } from '@/lib/apis/validators'
import { boolean, InferType } from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isHttpError, translateError } from '@/lib/apis/error'
import { Dialog } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { CommunicationChannel, TfaIntent } from '@/lib/apis/types'
import { DialogModal } from '@/shared/components/DialogModal'
import { useTfaStore, VerificationMethod } from '@/features/tfa/store'
import { StartTfaChallenge, VerifyTfaChallenge } from '@/lib/apis/tfa'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { useStore } from 'zustand'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { CheckDuplicateEmail, UpdateUserIHI } from '@/lib/apis/account'
import { PersonalDetailsFormContext } from '@/shared/request/PersonalDetailsFormContext'
import LoginModal from '@/features/auth/LoginModal'
import PrescriptionPersonalDetailsFields from '@/features/prescriptions/request/PrescriptionPersonalDetailsFields'
import { PrescriptionQuizFooter } from '@/features/prescriptions/request/PrescriptionQuizShell'
import { motion, useReducedMotion } from 'framer-motion'
import {
  softEditorialEase,
  softMotionDuration,
} from '@/shared/components/motion/motion-tokens'
import { UpdateUserProfile } from '@/lib/apis/profile'
import { GetProfileStatus } from '@medmax/api-client/profile'
import { IHIdentifier } from '@/lib/apis/types'
import { useSession } from 'next-auth/react'
import { useRouter } from 'next/navigation'

export type VerificationResult =
  | {
      address: string
      alreadyVerified: true
    }
  | {
      address: string
      alreadyVerified: false
      token: string
    }

export type PersonalDetailsFormState = {
  values: PersonalDetailsFormFields
  verification: VerificationOutcome
}

export type VerificationOutcome = {
  mobileVerificationResult?: VerificationResult
  verifiedEmail?: string
}

type ChannelAndAddress = {
  channel: CommunicationChannel
  address: string
}

type Props = {}

type Error = {
  title: string
  description: string
}

type ErrorAction = 'close' | 'retry-identifier' | 'correct-identifier'

const EXISTING_ACCOUNT_LOGIN_MESSAGE =
  'An account already exists with this email. Please sign in to continue.'

export const PersonalDetailsFormValidator = PatientProfileValidator.shape({
  acceptTnc: boolean()
    .default(false)
    .transform((value) => {
      if (!value) return false
      return value
    })
    .required()
    .isTrue('You have to accept our terms and conditions to proceed.'),
})

const ValidationSchema = PersonalDetailsFormValidator

export interface PersonalDetailsFormFields extends InferType<
  typeof PersonalDetailsFormValidator
> {}

export default function PersonalDetailsFormStage({}: Props) {
  const store = useContext(PersonalDetailsFormContext)
  if (!store)
    throw new Error(
      'PersonalDetailsFormStage must be used within a PersonalDetailsFormContextProvider',
    )

  const api = useApiClient()
  const queryClient = useQueryClient()
  const { update: updateSession } = useSession()
  const router = useRouter()
  const startTfaChallengeMutation = useMutation(StartTfaChallenge.mutation(api))
  const verifyTfaChallengeMutation = useMutation(
    VerifyTfaChallenge.mutation(api),
  )
  const checkDuplicateEmailMutation = useMutation(
    CheckDuplicateEmail.mutation(api),
  )
  const updateProfileMutation = useMutation(UpdateUserProfile.mutation(api))
  const updateUserIHIMutation = useMutation(UpdateUserIHI.mutation(api))

  const session = useStore(store, (s) => s.session)
  const profileFormState = useStore(store, (s) => s.profileFormState)
  const initialProfile = useStore(store, (s) => s.initialProfile)
  const profileStatus = useStore(store, (s) => s.profileStatus)
  const ihiVerificationState = useStore(store, (s) => s.ihiVerificationState)
  const verifiedIdentifierAttachmentPending = useStore(
    store,
    (s) => s.verifiedIdentifierAttachmentPending,
  )
  const setVerifiedIdentifierAttachmentPending = useStore(
    store,
    (s) => s.setVerifiedIdentifierAttachmentPending,
  )
  const completeVerifiedIdentifierAttachment = useStore(
    store,
    (s) => s.completeVerifiedIdentifierAttachment,
  )
  const returnToIdentifierCorrection = useStore(
    store,
    (s) => s.returnToIdentifierCorrection,
  )
  const updateProfileFormState = useStore(
    store,
    (s) => s.updateProfileFormState,
  )
  const onPrev = useStore(store, (s) => s.onPrev)
  const onNext = useStore(store, (s) => s.onNext)

  const [tfaError, setTfaError] = useState<Error>()
  const [showError, setShowError] = useState(false)
  const [errorAction, setErrorAction] = useState<ErrorAction>('close')
  const [showLoginDialog, setShowLoginDialog] = useState(false)
  const [loginMessage, setLoginMessage] = useState('')
  const identifierAttachmentPromise = useRef<Promise<void> | null>(null)
  const reduceMotion = useReducedMotion()

  const startTfaVerification = useTfaStore(
    (state) => state.startTfaVerification,
  )

  // Email is verified here means it passed the duplicate check
  const emailIsVerified =
    profileFormState.verification?.verifiedEmail ===
    profileFormState.values.email
  const mobileIsVerified =
    profileFormState.verification?.mobileVerificationResult?.address ===
    profileFormState.values.mobile

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    // The form owns edits while mounted; the store preserves them between steps.
    // Feeding watched edits back through `values` resets validation on each edit.
    defaultValues: profileFormState.values,
    resolver: yupResolver(ValidationSchema, { abortEarly: false }),
  })

  const { watch, handleSubmit, formState } = methods
  const { isSubmitting } = formState
  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      const casted = ValidationSchema.cast(value)
      updateProfileFormState((state) => ({ ...state, values: casted }))
    })

    return () => unsubscribe()
  }, [watch, updateProfileFormState])

  function showErrorModal(
    title: string,
    description: string,
    action: ErrorAction = 'close',
  ) {
    setShowError(true)
    setTfaError({ title, description })
    setErrorAction(action)
  }

  function closeLoginDialog() {
    setShowLoginDialog(false)
    setLoginMessage('')
  }

  function showLoginRequired() {
    setLoginMessage(EXISTING_ACCOUNT_LOGIN_MESSAGE)
    setShowLoginDialog(true)
  }

  function locallyVerifiedIdentifier(): IHIdentifier | null {
    const state = store!.getState().ihiVerificationState
    if (!state.verified) return null

    if (state.id === 'IHI') {
      return { type: 'ihi', ihi: state.verified.ihi }
    }

    return {
      type: 'med',
      number: state.verified.number,
      irn: state.verified.irn!,
      expiry: state.verified.expiry,
    }
  }

  function attachmentNeedsIdentifierCorrection(error: unknown): boolean {
    if (!isHttpError(error)) return false

    return (
      error.response.error === 'IHI_ERROR' ||
      error.response.error === 'INVALID_IHI' ||
      error.response.error === 'DUPLICATE_IHI'
    )
  }

  async function attachVerifiedIdentifierAfterLogin(): Promise<void> {
    if (identifierAttachmentPromise.current)
      return identifierAttachmentPromise.current

    const task = (async () => {
      const identifier = locallyVerifiedIdentifier()
      if (!identifier) {
        try {
          await updateSession()
        } catch {
          // The server refresh below remains the source of truth.
        } finally {
          router.refresh()
          closeLoginDialog()
        }
        return
      }

      setVerifiedIdentifierAttachmentPending(true)
      try {
        await updateUserIHIMutation.mutateAsync({ data: { identifier } })

        let refreshedProfileStatus = null
        try {
          refreshedProfileStatus = await queryClient.fetchQuery({
            ...GetProfileStatus.query(api),
            staleTime: 0,
          })
        } catch {
          // The identifier attachment is already committed. The server
          // component refresh below will reconcile status if this read fails.
        }

        completeVerifiedIdentifierAttachment(refreshedProfileStatus)
      } catch (error) {
        setVerifiedIdentifierAttachmentPending(false)
        const requiresCorrection = attachmentNeedsIdentifierCorrection(error)
        showErrorModal(
          "We couldn't add your Medicare details",
          requiresCorrection
            ? translateError(error)
            : 'Your details are still here. Try adding your Medicare details again.',
          requiresCorrection ? 'correct-identifier' : 'retry-identifier',
        )
      } finally {
        try {
          await updateSession()
        } catch {
          // The server refresh below remains the source of truth.
        } finally {
          router.refresh()
          closeLoginDialog()
        }
      }
    })()

    identifierAttachmentPromise.current = task
    try {
      await task
    } finally {
      if (identifierAttachmentPromise.current === task)
        identifierAttachmentPromise.current = null
    }
  }

  function handleErrorDismissal() {
    setShowError(false)
    if (errorAction === 'correct-identifier') {
      returnToIdentifierCorrection()
    } else if (errorAction === 'retry-identifier') {
      void attachVerifiedIdentifierAfterLogin()
    }
    setErrorAction('close')
  }

  function onSubmitFormError(err: any) {
    if (isHttpError(err)) {
      if (err.response.error === 'EXISTING_USER') showLoginRequired()
      else showErrorModal('Unexpected error happened', translateError(err))
    } else {
      showErrorModal(
        'Unexpected error happened',
        'Unexpected error occurred, please retry again.',
      )
    }
  }

  function buildVerificationMethodsFromChannels(
    channels: Array<ChannelAndAddress>,
    intent: TfaIntent,
  ): Array<VerificationMethod> {
    return channels.map((channel) => {
      if (channel.channel === 'Email')
        throw new Error('Email verification should not be started')

      return {
        ...channel,
        verify: async (token, code) => {
          const data = await verifyTfaChallengeMutation.mutateAsync({
            data: { ...channel, token, code, intent },
          })
          return data.token
        },
        onSuccessfulVerification: async (token) => {
          // Indicate on the UI that email is verified.
          updateProfileFormState((state) => ({
            ...state,
            verification: {
              ...state.verification,
              mobileVerificationResult: {
                address: channel.address,
                alreadyVerified: false,
                token,
              },
            },
          }))
        },
        resendCode: async () => {
          const data = await startTfaChallengeMutation.mutateAsync({
            data: {
              address: channel.address,
              channel: channel.channel,
              intent,
            },
          })
          return data.token
        },
        tokenState: {},
      }
    })
  }

  async function doTfaVerificationAndProceed(
    channels: Array<ChannelAndAddress>,
    intent: TfaIntent,
    values: PersonalDetailsFormFields,
  ) {
    const methods = buildVerificationMethodsFromChannels(channels, intent)

    const saveProfileAndProceed = async () => {
      if (session) {
        const current = store!.getState().profileFormState
        const { acceptTnc: _acceptTnc, ...profile } = values
        await updateProfileMutation.mutateAsync({
          profile: {
            ...profile,
            email: initialProfile?.email ?? values.email,
          },
          mobileVerificationToken:
            current.verification.mobileVerificationResult?.alreadyVerified ===
            false
              ? current.verification.mobileVerificationResult.token
              : undefined,
        })
      }
      onNext()
    }

    // There might not be any channel verification required if the form values don't change compared to the previous
    // verification.
    if (channels.length > 0) {
      await startTfaVerification(methods, saveProfileAndProceed)
    } else {
      await saveProfileAndProceed()
    }
  }

  async function onSubmitForm(
    values: PersonalDetailsFormFields,
  ): Promise<void> {
    if (verifiedIdentifierNeedsAttachment) {
      await attachVerifiedIdentifierAfterLogin()
      // Attaching can also remove this stage for a completed profile.
      // Let the refreshed stages determine where to go next.
      return
    }
    const channels: Array<ChannelAndAddress> = []

    if (!mobileIsVerified)
      channels.push({ channel: 'Sms', address: values.mobile })

    try {
      if (!session && !emailIsVerified) {
        const emailIsUpdated =
          initialProfile &&
          initialProfile.email &&
          values.email &&
          values.email !== initialProfile.email

        if (emailIsUpdated) {
          showErrorModal(
            'Mismatch Email',
            'The email you provided is different from the one you signed up with. If you want to update your email, you need to go to your profile page and update it there.',
          )
          return
        }

        const result = await checkDuplicateEmailMutation.mutateAsync(
          values.email,
        )
        if (result.duplicate) {
          showLoginRequired()
          return
        }

        // Update email state to verified
        updateProfileFormState((state) => ({
          ...state,
          verification: {
            ...state.verification,
            verifiedEmail: values.email,
          },
        }))
      }

      const intent = session ? 'PROFILE_UPDATE' : 'ACCOUNT_CREATION'
      await doTfaVerificationAndProceed(channels, intent, {
        ...values,
        email: session ? (initialProfile?.email ?? values.email) : values.email,
      })
    } catch (err) {
      onSubmitFormError(err)
    }
  }

  const verifiedFields = new Set<string>()
  if (emailIsVerified) verifiedFields.add('email')
  if (mobileIsVerified) verifiedFields.add('mobile')
  const verifiedIdentifierNeedsAttachment = Boolean(
    session && !profileStatus?.ihiSubmitted && ihiVerificationState.verified,
  )

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col gap-6 sm:gap-7">
        {!session && (
          <motion.aside
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 rounded-[14px] bg-[#F4F4F6] px-4 py-[13px]"
            initial={reduceMotion ? false : { opacity: 0, y: 10 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : {
                    duration: softMotionDuration.panel,
                    ease: softEditorialEase,
                  }
            }
          >
            <p className="min-w-0 text-[13px] font-medium leading-[18px] text-[#5C5C64] sm:text-[13.5px]">
              If you have an account, please sign in now.
            </p>
            <button
              className="ml-auto shrink-0 rounded-full bg-[#0B0B0F] px-[18px] py-[10px] text-[13px] font-semibold leading-4 text-white transition-colors duration-300 hover:bg-[#242428]"
              onClick={() => {
                setLoginMessage('')
                setShowLoginDialog(true)
              }}
              type="button"
            >
              Sign in
            </button>
          </motion.aside>
        )}

        {!session && (
          <motion.div
            animate={{ opacity: 1 }}
            className="relative flex items-center justify-center"
            initial={reduceMotion ? false : { opacity: 0 }}
            transition={
              reduceMotion
                ? { duration: 0 }
                : {
                    delay: 0.08,
                    duration: softMotionDuration.handoff,
                    ease: softEditorialEase,
                  }
            }
          >
            <div className="absolute inset-x-0 border-t border-[#ECECEF]" />
            <span className="relative bg-white px-3 text-[11.5px] leading-4 text-[#A0A0A8]">
              or fill in your details
            </span>
          </motion.div>
        )}

        <form noValidate onSubmit={handleSubmit(onSubmitForm)}>
          <PrescriptionPersonalDetailsFields
            disabled={isSubmitting || verifiedIdentifierAttachmentPending}
            emailDisabled={Boolean(session)}
            verifiedFields={verifiedFields}
          />
          <PrescriptionQuizFooter
            backDisabled={isSubmitting || verifiedIdentifierAttachmentPending}
            nextDisabled={verifiedIdentifierAttachmentPending}
            nextLabel="Review my request"
            nextLoading={isSubmitting || verifiedIdentifierAttachmentPending}
            note="Your information is encrypted and shared only with your doctor."
            onBack={onPrev}
            submit
          />
        </form>

        <ErrorModal
          error={tfaError}
          actionLabel={
            errorAction === 'correct-identifier'
              ? 'Check details'
              : errorAction === 'retry-identifier'
                ? 'Try again'
                : 'Close'
          }
          onDismiss={handleErrorDismissal}
          showError={showError}
        />
        <LoginModal
          initialEmail={profileFormState.values.email}
          initialMessage={loginMessage}
          onAuthenticated={attachVerifiedIdentifierAfterLogin}
          onClose={closeLoginDialog}
          open={showLoginDialog}
        />
      </div>
    </FormProvider>
  )
}

type ErrorProps = {
  error?: Error
  showError: boolean
  onDismiss: () => void
  actionLabel: string
}

const ErrorModal = forwardRef(function ErrorModal(
  { actionLabel, error, onDismiss, showError }: ErrorProps,
  ref: ForwardedRef<any>,
) {
  if (!error) {
    return null
  }

  const { title, description } = error
  return (
    <DialogModal show={showError} onClose={onDismiss} motionPreset="editorial">
      <div>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <FontAwesomeIcon
            className="h-6 w-6 text-red-600"
            aria-hidden="true"
            icon={faTriangleExclamation}
          />
        </div>
        <div className="mt-3 flex flex-col gap-y-3 text-center sm:mt-5">
          <Dialog.Title
            as="h3"
            className="text-base font-semibold leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <p className="text-gray-500">{description}</p>
          <div className="mt-5 sm:mt-6">
            <TextButton
              className="w-full"
              title={actionLabel}
              color="red"
              appearance="ring"
              onClick={onDismiss}
            />
          </div>
        </div>
      </div>
    </DialogModal>
  )
})
